import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useState } from 'react'
import dayjs from 'dayjs'
import {
  Route as RouteIcon,
  Settings,
  CalendarDays,
  Megaphone,
  Send,
  TriangleAlert,
  FileText,
  ClipboardCheck,
  FileClock,
} from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { SearchInput } from '@/components/ui/search-input'
import { TopBarBackIconButton } from '@/components/topbar/TopBarBackIconButton'
import {
  useQuickStartProgress,
  useCrewGetPending,
  useTripGetAll,
  useRosterGetCompanySummary,
  useDocumentGetExpired,
  useDocumentGetExpiring,
  useTasksToReview,
  useFlightLogDefectsToReview,
} from '@/lib/hooks'
import { useAnnouncements } from '@/lib/convex-hooks'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'

import { QuickStartCard } from '@/components/onboarding/QuickStartCard'
import { QuickStartGuideDrawer } from '@/components/onboarding/QuickStartGuideDrawer'
import { AppLayout } from '@/components/AppLayout'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { OperationsSearchDialogOverlay } from '@/components/operations/OperationsSearchDialogOverlay'
import { PendingInvitesDrawer } from '@/components/operations/PendingInvitesDrawer'
import { OperationsReviewDocumentsDrawer } from '@/components/operations/OperationsReviewDocumentsDrawer'
import { OperationsTasksReviewDrawer } from '@/components/operations/OperationsTasksReviewDrawer'
import { DefectsLoggedDrawer } from '@/components/operations/DefectsLoggedDrawer'

const adminSearchSchema = z.object({
  search: z.string().optional(),
  // Deep link from the "Defect logged" notification (?defects=1)
  defects: z.coerce.boolean().optional(),
})

export const Route = createFileRoute('/_auth/operations/')({
  validateSearch: adminSearchSchema,
  component: AdminDashboard,
})

interface AdminCard {
  id: string
  icon: React.ReactNode
  label: string
  description: string
  to: string
  badge?: React.ReactNode
}

function AdminDashboard() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const { search: initialSearch, defects: initialDefects } = Route.useSearch()
  const { data: quickStartProgress } = useQuickStartProgress()
  const [isQuickStartDrawerOpen, setIsQuickStartDrawerOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(!!initialSearch)
  const [isInvitesOpen, setIsInvitesOpen] = useState(false)
  const [isInvalidDocsOpen, setIsInvalidDocsOpen] = useState(false)
  const [isTasksReviewOpen, setIsTasksReviewOpen] = useState(false)
  const [isDefectsOpen, setIsDefectsOpen] = useState(!!initialDefects)

  const startDate = dayjs().startOf('month').format('YYYY-MM-DD')
  const endDate = dayjs().endOf('month').format('YYYY-MM-DD')
  const monthLabel = dayjs().format('MMM YYYY').toUpperCase()

  const { data: summary, isLoading: isSummaryLoading } =
    useRosterGetCompanySummary(startDate, endDate)
  const { data: pendingUsers = [] } = useCrewGetPending()
  const { data: trips = [] } = useTripGetAll()
  const { data: expiredCrewDocs = [] } = useDocumentGetExpired({ category: 'Crew' })
  const { data: expiringCrewDocs = [] } = useDocumentGetExpiring({
    category: 'Crew',
    daysAhead: 30,
  })
  const { data: expiredComplianceDocs = [] } = useDocumentGetExpired({
    category: 'Compliance',
  })
  const { data: expiringComplianceDocs = [] } = useDocumentGetExpiring({
    category: 'Compliance',
    daysAhead: 30,
  })
  const { data: tasksToReview = [] } = useTasksToReview()
  const { data: defectLogs = [] } = useFlightLogDefectsToReview()
  const { data: currentCrew } = useCurrentCrewMember()
  const announcements = useAnnouncements(currentCrew?.companyId, currentCrew?.id)
  const announcementsCount = announcements?.length ?? 0

  const expiredDocsCount = expiredCrewDocs.length + expiredComplianceDocs.length
  const expiringDocsCount = expiringCrewDocs.length + expiringComplianceDocs.length
  const invalidDocsCount = expiredDocsCount + expiringDocsCount
  const unreadTasksCount = tasksToReview.filter(
    (t: { status: string }) => t.status !== 'Read'
  ).length
  // The drawer lists all deferred-defect logs; the badge only counts unviewed
  const unviewedDefectsCount = defectLogs.filter(
    (l: { defectsViewedAt: string | Date | null }) => !l.defectsViewedAt
  ).length

  const showQuickStartCard =
    quickStartProgress &&
    !quickStartProgress.quickStarted &&
    !quickStartProgress.allCompleted

  const handleSettingsClick = () => {
    navigate({ to: '/operations/settings' })
  }

  const drawers = (
    <>
      <QuickStartGuideDrawer
        isOpen={isQuickStartDrawerOpen}
        onClose={() => setIsQuickStartDrawerOpen(false)}
        steps={quickStartProgress?.steps}
      />

      <OperationsSearchDialogOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        initialSearch={initialSearch}
      />

      <PendingInvitesDrawer
        open={isInvitesOpen}
        onOpenChange={setIsInvitesOpen}
      />

      <OperationsReviewDocumentsDrawer
        open={isInvalidDocsOpen}
        onOpenChange={setIsInvalidDocsOpen}
      />

      <OperationsTasksReviewDrawer
        open={isTasksReviewOpen}
        onOpenChange={setIsTasksReviewOpen}
      />

      <DefectsLoggedDrawer
        open={isDefectsOpen}
        onOpenChange={setIsDefectsOpen}
      />
    </>
  )

  const overviewModules = (
    <>
      {/* Hero: Operations overview */}
        <div className="operations-hero-card-bg relative flex w-full items-center gap-4 overflow-hidden rounded-2xl p-4">
          <div className="relative z-10 flex w-28 shrink-0 flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-content-brand-light leading-4">
              {monthLabel}
            </span>
            <h2 className="text-xl font-bold leading-5 tracking-[-1px] text-content-primary-white">
              Operations overview
            </h2>
            <span className="text-[10px] font-semibold leading-4 text-content-brand-light">
              Upcoming
            </span>
          </div>
          <div className="relative z-10 flex flex-1 items-center gap-4">
            <StatTile
              label="FLIGHTS"
              value={isSummaryLoading ? null : summary?.totalFlights ?? 0}
              sub="This month"
            />
            <StatTile
              label="ON DUTY"
              value={isSummaryLoading ? null : summary?.totalOnDutyCrew ?? 0}
              sub="Crew"
            />
          </div>
        </div>

        {/* MANAGE */}
        <section className="flex flex-col gap-3">
          <p className="text-xs pt-4 font-semibold uppercase tracking-[1.5px] text-content-primary-subtle-mid">
            Manage
          </p>
          <div className="flex flex-col gap-2">
            <AdminModuleCard
              card={{
                id: 'schedule',
                icon: <CalendarDays className="w-8 h-8 text-content-brand-bold" />,
                label: 'Schedule',
                description: 'Rosters & duty',
                to: '/operations/rosters',
                badge:
                  summary?.totalOnDutyCrew && summary.totalOnDutyCrew > 0 ? (
                    <KpiBadge value={summary.totalOnDutyCrew} />
                  ) : undefined,
              }}
            />
            <AdminModuleCard
              card={{
                id: 'trips',
                icon: <RouteIcon className="w-8 h-8 text-content-brand-bold" />,
                label: 'Trips',
                description: 'Create & run trips',
                to: '/operations/trips',
                badge:
                  trips.length > 0 ? <KpiBadge value={trips.length} /> : undefined,
              }}
            />
            <AdminModuleCard
              card={{
                id: 'announcements',
                icon: <Megaphone className="w-8 h-8 text-content-brand-bold" />,
                label: 'Announcements',
                description: 'Send crew updates',
                to: '/operations/announcements',
                badge:
                  announcementsCount > 0 ? (
                    <KpiBadge value={announcementsCount} />
                  ) : undefined,
              }}
            />
            <AdminModuleCard
              card={{
                id: 'activity',
                icon: <FileClock className="w-8 h-8 text-content-brand-bold" />,
                label: 'Activity Log',
                description: 'Who changed what',
                to: '/operations/activity',
              }}
            />
          </div>
        </section>

        {/* UPDATES */}
        <section className="flex flex-col gap-3">
          <p className="text-xs pt-4 font-semibold uppercase tracking-[1.5px] text-content-primary-subtle-mid">
            Updates
          </p>
          <Card className="flex flex-col gap-1 p-2">
            {defectLogs.length > 0 && (
              <UpdateRow
                title="Defects Logged"
                subtitle={`${defectLogs.length} ${defectLogs.length === 1 ? 'defect report' : 'defect reports'}${unviewedDefectsCount > 0 ? ` | ${unviewedDefectsCount} new` : ''}`}
                count={defectLogs.length}
                showDot={unviewedDefectsCount > 0}
                dotClassName="bg-content-negative-bold"
                icon={<TriangleAlert className="size-5 text-content-negative-bold" />}
                onClick={() => setIsDefectsOpen(true)}
              />
            )}
            <UpdateRow
              title="Documents"
              subtitle={
                invalidDocsCount > 0
                  ? `${expiredDocsCount} expired | ${expiringDocsCount} Expiring soon`
                  : 'No expiring documents'
              }
              count={invalidDocsCount}
              dotClassName="bg-content-negative-bold"
              icon={<FileText className="size-5 text-content-negative-bold" />}
              onClick={() => setIsInvalidDocsOpen(true)}
            />
            <UpdateRow
              title="Review Tasks"
              subtitle={
                unreadTasksCount > 0
                  ? `${unreadTasksCount} tasks submitted`
                  : 'No tasks to review'
              }
              count={unreadTasksCount}
              dotClassName="bg-content-info-bold"
              icon={<ClipboardCheck className="size-5 text-content-info-bold" />}
              onClick={() => setIsTasksReviewOpen(true)}
            />
            <UpdateRow
              title="Invites"
              subtitle={
                pendingUsers.length > 0
                  ? `${pendingUsers.length} pending logins`
                  : 'All logged in'
              }
              count={pendingUsers.length}
              dotClassName="bg-background-notice-bold"
              icon={<Send className="size-5 text-content-brand-bold" />}
              onClick={() => setIsInvitesOpen(true)}
            />
          </Card>
        </section>
    </>
  )

  if (isDesktop) {
    return (
      <>
        {drawers}
        <DesktopPage>
          <DesktopPageHeader
            title="Operations"
            subtitle="Control rosters and more…"
            action={
              <Button
                roundness="round"
                variant="secondary"
                size="sm"
                className="size-9 p-0"
                onClick={handleSettingsClick}
              >
                <Settings className="size-4 text-content-primary-bold" />
              </Button>
            }
          />
          {showQuickStartCard && (
            <QuickStartCard onClick={() => setIsQuickStartDrawerOpen(true)} />
          )}
          {overviewModules}
        </DesktopPage>
      </>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          title="Operations"
          showBackButton={false}
          icon={
            <Button roundness="round" variant="secondary" size="sm" className="size-8 p-0">
              <Settings className="size-4 text-content-primary-bold" />
            </Button>
          }
          onIconClick={handleSettingsClick}
        />
      }
    >
      {drawers}
      <div className="flex flex-col gap-6 pb-28">
        <SearchInput
          asButton
          placeholder="Search..."
          onClick={() => setIsSearchOpen(true)}
        />

        {showQuickStartCard && (
          <QuickStartCard onClick={() => setIsQuickStartDrawerOpen(true)} />
        )}

        {overviewModules}
      </div>
    </AppLayout>
  )
}

function StatTile({
  label,
  value,
  sub,
}: {
  label: string
  value: number | null
  sub: string
}) {
  return (
    <div className="flex flex-1 flex-col items-start rounded-lg bg-white/11 p-2">
      <span className="text-xs font-semibold leading-4 text-content-brand-light">
        {label}
      </span>
      <span className="text-[22px] font-bold leading-7 tracking-[-1px] text-content-primary-white">
        {value === null ? '—' : value}
      </span>
      <span className="text-[10px] font-semibold leading-4 text-content-brand-light">
        {sub}
      </span>
    </div>
  )
}

function KpiBadge({ value }: { value: number }) {
  return (
    <div className="flex h-[25px] min-w-[25px] items-center justify-center rounded-lg bg-surface-L2 px-[9px] py-[3px]">
      <span className="text-xs font-medium text-content-primary-bold">{value}</span>
    </div>
  )
}

interface UpdateRowProps {
  title: string
  subtitle: string
  count: number
  /** Unread dot on the badge; defaults to count > 0. */
  showDot?: boolean
  dotClassName: string
  icon?: React.ReactNode
  onClick: () => void
}

function UpdateRow({ title, subtitle, count, showDot, dotClassName, icon, onClick }: UpdateRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full min-h-[36px] items-center gap-3 rounded-md px-3 py-2 text-left"
    >
      {icon && <div className="shrink-0">{icon}</div>}
      <div className="flex flex-1 flex-col items-start gap-1 min-w-0">
        <p className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px]">
          {title}
        </p>
        <p className="text-sm text-content-primary-subtle leading-5">{subtitle}</p>
      </div>
      <div className="relative shrink-0">
        <KpiBadge value={count} />
        {(showDot ?? count > 0) && (
          <div
            className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ${dotClassName}`}
          />
        )}
      </div>
    </button>
  )
}

function AdminModuleCard({
  card,
  onClick,
}: {
  card: AdminCard
  onClick?: () => void
}) {
  const content = (
    <Card interactive className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="bg-background-brand-subtle rounded-lg p-2 shrink-0">
          {card.icon}
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <p className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px]">
            {card.label}
          </p>
          <p className="text-sm text-content-primary-subtle leading-5">
            {card.description}
          </p>
        </div>
        {card.badge && <div className="shrink-0">{card.badge}</div>}
      </div>
    </Card>
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="w-full text-left">
        {content}
      </button>
    )
  }

  return (
    <Link to={card.to} className="w-full">
      {content}
    </Link>
  )
}
